<script setup lang="ts">
import { computed, onMounted } from 'vue';

import { formatDateTime, formatSigned } from './format';
import CurrencyIcon from '@/ui/CurrencyIcon.vue';
import { api } from '@/net/api';
import { useAccount } from '@/state/account';

/**
 * Purchases: diamonds bought with money, and credits converted out of them.
 *
 * **It is the ledger, filtered — not a table of its own.** The template's
 * `CurrencyLedger` already records every movement of either currency with a
 * reason and a running balance, so this pane needs no storage and cannot disagree
 * with what the admin panel shows. `AccountService.purchases` narrows it to the
 * three reasons that are actually purchases; an upgrade or a part paid for inside
 * the game is a *spend*, and the garage already shows those as owned items.
 *
 * **Why this sits beside Billing rather than inside it.** They answer different
 * questions. Billing is the money that left the bank, in invoices; this is what
 * arrived in the account and where it went afterwards — including the
 * diamonds-to-credits conversion, which moves in-game currency and has no invoice
 * at all. Rows that *do* have one link to it, through the number the purchase
 * wrote into its own ledger reference.
 */
const account = useAccount();

onMounted(() => {
  account.loadPurchases().catch(() => {});
});

const pages = computed(() => Math.max(1, Math.ceil(account.purchaseTotal.value / 20)));

/**
 * The reason, worded for a player.
 *
 * The ledger's own `description` is a machine string (`pack_purchase`), and a
 * history reading `custom_pack_purchase` is a log rather than a statement. An
 * unrecognised reason falls through to the raw value rather than being hidden or
 * blanked — a purchase history with a gap in it is worse than one with an
 * unpolished row, and the server maps the same set with the same fallback.
 */
const LABELS: Record<string, string> = {
  pack_purchase: 'Diamond pack',
  custom_pack_purchase: 'Diamonds, custom amount',
  diamond_conversion: 'Converted to credits',
};

function label(description: string): string {
  return LABELS[description] ?? description;
}
</script>

<template>
  <div class="pane" data-test="pane-purchases">
    <!-- Named for what the block is rather than for the section it sits in —
         the rail chip already says Purchases. See `LegalPane`. -->
    <section class="block">
      <h3>Purchase history</h3>
      <p class="hint">
        Diamonds bought with money, and credits converted out of them. Anything
        spent inside the game shows up in the garage as the thing you bought.
      </p>

      <div v-if="account.purchases.value.length > 0" class="table-wrap">
        <table data-test="purchases-table">
          <thead>
            <tr>
              <th>When</th>
              <th>What</th>
              <th class="num">Amount</th>
              <th class="num">Balance after</th>
              <th>Invoice</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in account.purchases.value" :key="row.id" :data-kind="row.kind">
              <td class="when">{{ formatDateTime(row.at) }}</td>
              <td>{{ label(row.description) }}</td>
              <td class="num" :class="{ spend: row.amount < 0 }">
                {{ formatSigned(row.amount) }}
                <CurrencyIcon
                  :kind="row.currencyType === 'PREMIUM' ? 'diamonds' : 'credits'"
                />
              </td>
              <td class="num">{{ row.balanceAfter.toLocaleString('en-GB') }}</td>
              <td>
                <a
                  v-if="row.invoiceNumber"
                  class="mono link"
                  :href="api.invoicePdfUrl(row.invoiceNumber)"
                  target="_blank"
                  rel="noopener"
                  :data-test="`purchase-invoice-${row.invoiceNumber}`"
                >
                  {{ row.invoiceNumber }}
                </a>
                <span v-else class="none">&mdash;</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p v-else-if="account.loaded.value.purchases" class="empty" data-test="purchases-empty">
        Nothing bought yet.
      </p>
      <p v-else class="empty">Loading…</p>

      <div v-if="pages > 1" class="pager">
        <button
          type="button"
          class="ghost"
          :disabled="account.purchasePage.value <= 1 || account.busy.value"
          @click="account.loadPurchases(account.purchasePage.value - 1)"
        >
          Newer
        </button>
        <span>Page {{ account.purchasePage.value }} of {{ pages }}</span>
        <button
          type="button"
          class="ghost"
          :disabled="account.purchasePage.value >= pages || account.busy.value"
          @click="account.loadPurchases(account.purchasePage.value + 1)"
        >
          Older
        </button>
      </div>
    </section>
  </div>
</template>

<style scoped>
@import './pane.css';

.when {
  white-space: nowrap;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.76rem;
  white-space: nowrap;
}

.link {
  color: var(--accent);
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}

.link:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

/* An em dash is the whole cell here, so it has to stay legible as "no invoice"
   rather than fading into the rule under it. */
.none {
  color: #7d8ca1;
}

/* A grant reads in ordinary text and a spend in the hostile red this HUD uses
   everywhere for a loss, so the direction is legible without reading the sign. */
.spend {
  color: #ff8478;
}

/* The mark's own root classes — it has three branches (drawn coin, gem art, drawn
   gem fallback) and no shared wrapper class, so all three are named. */
.num :deep(.coin),
.num :deep(.gem),
.num :deep(.gem-art) {
  margin-left: 0.2rem;
}

.pager .ghost {
  margin-top: 0;
}
</style>
